import { inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { ICustomerActivationResponse, ICustomerRegistrationResponse, IResetPasswordResponse, IUpdatePasswordPayload, IUpdatePasswordResponse, SOAPRequest, User } from '../models/model';
import { Observable, BehaviorSubject, throwError } from 'rxjs';
import { DialogService } from './dialog.service';
import { TranslateService } from '@ngx-translate/core';

interface PartnerLoginError extends Error {
  partnerLoginAttempt?: boolean;
}


@Injectable({ providedIn: 'root' })
export class AuthenticationService {
  private router = inject(Router);
  private http = inject(HttpClient);

  private _isloggedInSubject = new BehaviorSubject<boolean>(false);
  private user = signal<User | null>(null);
  private dialogService = inject(DialogService);
  private translateService = inject(TranslateService);

  
  public isLoggedIn$ = this._isloggedInSubject.asObservable();

  public get isLoggedIn(): boolean {
    //console.log('==> isLoggedIn ', this.user() !== null);
    //Note: this function is called many times. Don't add any additional logic.
    return this.user() !== null;  
  }

  //TODO: set    this._isloggedInSubject   
  public get loggedInUser(): User | null {
    if (this.user() === null) {
      console.log('==> loggedInUser is null, checking session');
      this.checkSession().subscribe({
        next: (user) => this.setUser(user),
        error: () => this.setUser(null)
      }); 
    }
    
    return this.user();
  }

  public checkCookie() {
    if (this.user() === null) {
      this.checkSession().subscribe({
        next: (user) => this.setUser(user),
        error: () => this.setUser(null)
      }); 
    }
  }

  checkSession() {
    //console.log('==> checking session');
    return this.http.get<User>(`${environment.apiUrl}/auth/me`, { withCredentials: true });
  }

  public setUser(user: User | null): void {
    if(user === null) 
      return;
    // Default Language to 'E' if not supplied
    if (!user.Language) {
      user.Language = 'E';
    }
    this.user.set(user);
    // Default to English if not 'F'
    this.setLocale(user.Language === 'F' ? 'fr' : 'en');  
    this._isloggedInSubject.next(true);    
  }


  setLocale(language: 'en' | 'fr') {
    //console.log('==> new locale', language);
    window.localStorage.setItem('language', language);
    this.translateService.use(language);
    
  }

  //returns true if language is 'E' otherwise false
  isEnglish(): boolean {
    return this.loggedInUser !== null && this.loggedInUser.Language === 'E';
  }


  logout() {
    // Call backend to clear the access_token cookie
    this.http.post(`${environment.apiUrl}/auth/logout`, {}, { withCredentials: true }).subscribe({
      next: () => { //make logout call sync so customer-portal-landing pages doesn't try to load products and error
        //clear local state
        this.user.set(null);
        this._isloggedInSubject.next(false);
        this.setLocale('en');
        localStorage.removeItem('PRODUCT_NAME');
        this.router.navigate(['/customer-portal']);
    
      },
      //error: () => {},
    });

  }
  
  // invalidSession() {
  //   localStorage.setItem('language', 'en');
  //   window.location.href = '/'
  // }


  navigateToSign() {
    this.logout();
    this.dialogService.openSignInDialog();
    // this.router.navigate(['/'])
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  authenticateCaptchaToken(payload:any){
  //   const headers = new HttpHeaders({
  //   'x-goog-api-key': '6LfIAWUrAAAAABuHBe5Pc-4cAEOI0Zc_vlv47nph',
  //   'Content-Type': 'application/json'
  // });
    return this.http.post<unknown>('https://recaptchaenterprise.googleapis.com/v1/projects/my-project-2748-1746541259662/assessments?key=6LfIAWUrAAAAABuHBe5Pc-4cAEOI0Zc_vlv47nph',payload)
  }

  
//API callouts
  
  login(payload: { EmailAddress: string; Password: string }) {
    const requestBody: SOAPRequest = {
      payload
    };
    return this.http
    .post<User>(`${environment.apiUrl}/auth/sign-in`, requestBody, {
      withCredentials: true,
      responseType: 'json'
    })
    .pipe(
      map((response: User) => {
        if (!response || !response.id) {
          throw new Error(this.translateService.instant('error.invalidResponseFromServer'));
        }

        this.setUser(response); 
        return response;
      }),
      catchError((error) => {
          //server returns https response codes, not SAP responses - map SAP responses on
          switch (error.status) {
            case 401:
              throw new Error(this.translateService.instant('error.invalidUsernamePassword'));
            case 422: {
              // Indicate that this is a partner login attempt error
              const partnerError: PartnerLoginError = new Error(this.translateService.instant('error.partnerLoginAttempt'));
              partnerError.partnerLoginAttempt = true;
              throw partnerError;
            }
            case 424:
              throw new Error(this.translateService.instant('error.noAssociatedCompanyContactSupport'));
            case 423:
              throw new Error(this.translateService.instant('error.accountLockedContactAdmin'));
            default:
              throw new Error(this.translateService.instant('error.unexpectedErrorTryAgain'));
          }
        
      })
    );
  }

  signUp(payload: object): Observable<ICustomerRegistrationResponse> {
    const requestBody: SOAPRequest = {
      payload 
    };

    return this.http
      .post<ICustomerRegistrationResponse>(`${environment.apiUrl}/auth/sign-up`, requestBody, { withCredentials: true });
  }

  resetPassword(payload: {emailaddress: string, useralias: string}){
    const requestBody: SOAPRequest = {
      payload
    };
    return this.http
      .post<IResetPasswordResponse>(`${environment.apiUrl}/auth/reset-password`, requestBody, { withCredentials: true })
  }

  customerActivation(payload: {guid: string}): Observable<ICustomerActivationResponse> {
    const requestBody: SOAPRequest = {
      payload
   };
    return this.http.post<ICustomerActivationResponse>(`${environment.apiUrl}/auth/customer-activation`, requestBody, { withCredentials: true })
    .pipe(
      map((response: ICustomerActivationResponse) => {
        if (!response['MT_CustomerActivation_Resp']) {
          throw Error('error.invalidResponseFromServer')
        }
        return response;
      }),
      catchError((err) => throwError(() => err))
    )
  }

  updatePassword(payload: IUpdatePasswordPayload){
    console.log('authentication service reset password');
    const requestBody: SOAPRequest = {
      payload
    };
    return this.http
      .post<IUpdatePasswordResponse>(`${environment.apiUrl}/auth/update-password`, requestBody, { withCredentials: true })
  }
}
